// D&D 5e SRD Feats Database
// Complete list of Player's Handbook feats with effects

import { EQUIPMENT_SLOTS } from '../constants/enums';
// [PHASE B / #013 — STRING-SIN SWEEP / RULE 11]: Single source of truth for
//   polearm detection (was duplicated below — see Polearm Master).
import { isPolearm } from '../utils/weaponClassification';

export const SRD_FEATS = {
  // ========================================
  // COMBAT FEATS
  // ========================================

  'alert': {
    id: 'alert',
    name: 'Alert',
    description: 'Always on the lookout for danger, you gain the following benefits.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'You gain a +5 bonus to initiative.',
      'You can\'t be surprised while you are conscious.',
      'Other creatures don\'t gain advantage on attack rolls against you as a result of being unseen by you.'
    ],
    // [PHASE 1.2 - NOV 14, 2025]: Added unified effects schema for EffectRegistry integration
    // [PHASE 3.3 - NOV 18, 2025]: Completed migration with remaining effects
    effects: [
      {
        type: 'initiative',
        operation: 'add',
        value: 5,
        condition: 'always'
      },
      {
        type: 'feature',
        operation: 'grant',
        value: 'cannot_be_surprised',
        condition: 'always'
      },
      {
        type: 'feature',
        operation: 'grant',
        value: 'no_advantage_from_unseen',
        condition: 'always'
      }
    ],
    passiveEffects: {
      initiative: 5, // Keep for backward compatibility during migration
      cannotBeSurprised: true,
      noAdvantageFromHidden: true
    },
    activeAbilities: null,
    tags: ['combat', 'defensive', 'perception']
  },

  'crossbow_expert': {
    id: 'crossbow_expert',
    name: 'Crossbow Expert',
    description: 'Thanks to extensive practice with the crossbow, you gain the following benefits.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'You ignore the loading property of crossbows with which you are proficient.',
      'Being within 5 feet of a hostile creature doesn\'t impose disadvantage on your ranged attack rolls.',
      'When you use the Attack action and attack with a one-handed weapon, you can use a bonus action to attack with a hand crossbow you are holding.'
    ],
    // [PHASE 3.1 - NOV 18, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'weaponProperty',
        target: 'crossbow',
        operation: 'ignore',
        value: 'loading',
        condition: 'always'
      },
      {
        type: 'combatAdvantage',
        target: 'ranged_in_melee',
        operation: 'remove_disadvantage',
        value: true,
        condition: 'always'
      }
    ],
    passiveEffects: {
      ignoreLoading: ['crossbow'], // Keep for backward compatibility during migration
      noRangedDisadvantageInMelee: true
    },
    activeAbilities: {
      bonusAttack: {
        name: 'Crossbow Bonus Attack',
        actionType: 'bonus',
        description: 'Attack with hand crossbow after attacking with one-handed weapon',
        usesPerTurn: 1
      }
    },
    tags: ['combat', 'ranged', 'offensive']
  },

  'defensive_duelist': {
    id: 'defensive_duelist',
    name: 'Defensive Duelist',
    description: 'When you are wielding a finesse weapon with which you are proficient and another creature hits you with a melee attack, you can use your reaction to add your proficiency bonus to your AC for that attack.',
    source: 'PHB',
    prerequisites: {
      abilities: { dex: 13 }
    },
    benefits: [
      'Use reaction to add proficiency bonus to AC against one melee attack while wielding a finesse weapon.'
    ],
    // [PHASE 3.1 - NOV 18, 2025]: This feat's functionality is entirely in activeAbilities (reaction-based)
    // No passive effects to convert to unified schema
    effects: [],
    passiveEffects: null,
    activeAbilities: {
      parry: {
        name: 'Defensive Parry',
        actionType: 'reaction',
        description: 'Add proficiency bonus to AC against one melee attack',
        requirement: 'Wielding finesse weapon',
        effect: 'acBonus:proficiency'
      }
    },
    tags: ['combat', 'defensive', 'reaction']
  },

  'dual_wielder': {
    id: 'dual_wielder',
    name: 'Dual Wielder',
    description: 'You master fighting with two weapons, gaining the following benefits.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'You gain a +1 bonus to AC while you are wielding a separate melee weapon in each hand.',
      'You can use two-weapon fighting even when the one-handed melee weapons you are wielding aren\'t light.',
      'You can draw or stow two one-handed weapons when you would normally be able to draw or stow only one.'
    ],
    // [PHASE 1.2 - NOV 14, 2025]: Added unified effects schema for EffectRegistry integration
    // [PHASE 4.1 - NOV 18, 2025]: Completed migration with remaining effects
    // Conditional effect: only applies when dual wielding
    effects: [
      {
        type: 'ac',
        operation: 'add',
        value: 1,
        condition: {
          when: 'dualWielding',
          check: (character, context) => {
            // [Phase 4b-infra APR 2026]: Prefer context.dualWielding (set by
            // EffectCalculator._buildContext). Legacy fallback reads
            // character.inventory — NOT character.equipment, which never
            // existed on real character data and silently dropped this AC
            // bonus in production.
            if (context && typeof context.dualWielding === 'boolean') return context.dualWielding;
            const inv = character.inventory || [];
            const mainHand = inv.find(i => i?.equipped &&
              (i?.equipSlot === EQUIPMENT_SLOTS.MAIN_HAND || i?.equipSlot === 'main_hand') &&
              i?.category === 'weapons');
            const offHand = inv.find(i => i?.equipped &&
              (i?.equipSlot === EQUIPMENT_SLOTS.OFF_HAND || i?.equipSlot === 'off_hand') &&
              i?.category === 'weapons');
            return Boolean(mainHand && offHand);
          }
        }
      },
      {
        type: 'feature',
        operation: 'grant',
        value: 'dual_wield_non_light',
        condition: 'always'
      },
      {
        type: 'feature',
        operation: 'grant',
        value: 'quick_draw_2_weapons',
        condition: 'always'
      }
    ],
    passiveEffects: {
      acBonusWhileDualWielding: 1, // Keep for backward compatibility during migration
      dualWieldNonLight: true,
      quickDraw: 2
    },
    activeAbilities: null,
    tags: ['combat', 'offensive', 'defensive']
  },

  'great_weapon_master': {
    id: 'great_weapon_master',
    name: 'Great Weapon Master',
    description: 'You\'ve learned to put the weight of a weapon to your advantage, letting its momentum empower your strikes.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'On your turn, when you score a critical hit with a melee weapon or reduce a creature to 0 hit points with one, you can make one melee weapon attack as a bonus action.',
      'Before you make a melee attack with a heavy weapon that you are proficient with, you can choose to take a -5 penalty to the attack roll. If the attack hits, you add +10 to the attack\'s damage.'
    ],
    // [PHASE 5.2 - NOV 18, 2025]: Added unified effects schema
    // This feat's functionality is entirely in activeAbilities and toggleEffects
    effects: [],
    passiveEffects: null,
    activeAbilities: {
      bonusAttackOnCrit: {
        name: 'Cleaving Strike',
        actionType: 'bonus',
        description: 'Make bonus attack after critical hit or reducing enemy to 0 HP',
        trigger: 'critical_or_kill'
      }
    },
    toggleEffects: {
      powerAttack: {
        name: 'Power Attack',
        description: '-5 to hit, +10 damage with heavy weapons',
        attackMod: -5,
        damageMod: 10,
        appliesTo: 'heavy_melee'
      }
    },
    tags: ['combat', 'offensive', 'damage']
  },

  'healer': {
    id: 'healer',
    name: 'Healer',
    description: 'You are an able physician, allowing you to mend wounds quickly and get your allies back in the fight.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'When you use a healer\'s kit to stabilize a dying creature, that creature also regains 1 hit point.',
      'As an action, you can spend one use of a healer\'s kit to tend to a creature and restore 1d6 + 4 hit points to it, plus additional hit points equal to the creature\'s maximum number of Hit Dice. The creature can\'t regain hit points from this feat again until it finishes a short or long rest.'
    ],
    // [PHASE 4.2 - NOV 18, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'feature',
        operation: 'grant',
        value: 'healers_kit_revive_1hp',
        condition: 'always'
      }
    ],
    passiveEffects: {
      healersKitRevive: true  // Keep for backward compatibility during migration
    },
    activeAbilities: {
      healingTreatment: {
        name: 'Healing Treatment',
        actionType: 'action',
        description: 'Heal 1d6+4+HD hit points (once per rest per creature)',
        resource: 'healers_kit',
        healFormula: '1d6+4+HD',
        cooldown: 'per_creature_per_rest'
      }
    },
    tags: ['support', 'healing']
  },

  'inspiring_leader': {
    id: 'inspiring_leader',
    name: 'Inspiring Leader',
    description: 'You can spend 10 minutes inspiring your companions, shoring up their resolve to fight.',
    source: 'PHB',
    prerequisites: {
      abilities: { cha: 13 }
    },
    benefits: [
      'Spend 10 minutes to grant up to 6 creatures (including yourself) temporary hit points equal to your level + Charisma modifier. Once per short or long rest.'
    ],
    // [PHASE 4.2 - NOV 18, 2025]: This feat's functionality is entirely in activeAbilities (inspiring speech)
    // No passive effects to convert to unified schema
    effects: [],
    passiveEffects: null,
    activeAbilities: {
      inspiringSpeech: {
        name: 'Inspiring Speech',
        actionType: 'ritual',
        duration: '10 minutes',
        description: 'Grant level + CHA temp HP to 6 creatures',
        tempHpFormula: 'level+cha',
        targets: 6,
        uses: 1,
        rechargeOn: 'shortRest'
      }
    },
    tags: ['support', 'leadership', 'temporary_hp']
  },

  'lucky': {
    id: 'lucky',
    name: 'Lucky',
    description: 'You have inexplicable luck that seems to kick in at just the right moment.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'You have 3 luck points. Whenever you make an attack roll, an ability check, or a saving throw, you can spend one luck point to roll an additional d20.',
      'You can also spend one luck point when an attack roll is made against you. Roll a d20 and choose whether the attack uses the attacker\'s roll or yours.',
      'You regain your expended luck points when you finish a long rest.'
    ],
    // [PHASE 3.3 - NOV 18, 2025]: This feat's functionality is entirely in activeAbilities (luck points)
    // No passive effects to convert to unified schema
    effects: [],
    passiveEffects: null,
    activeAbilities: {
      luckPoints: {
        name: 'Luck Points',
        description: 'Reroll any d20 or force enemy reroll',
        uses: 3,
        rechargeOn: 'longRest',
        icon: '🍀'
      }
    },
    tags: ['utility', 'defensive', 'offensive', 'luck']
  },

  'mage_slayer': {
    id: 'mage_slayer',
    name: 'Mage Slayer',
    description: 'You have practiced techniques useful in melee combat against spellcasters.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'When a creature within 5 feet of you casts a spell, you can use your reaction to make a melee weapon attack against that creature.',
      'When you damage a creature that is concentrating on a spell, that creature has disadvantage on the saving throw it makes to maintain its concentration.',
      'You have advantage on saving throws against spells cast by creatures within 5 feet of you.'
    ],
    // [PHASE 5.4 - NOV 18, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'feature',
        operation: 'grant',
        value: 'target_concentration_disadvantage_when_damaged',
        condition: 'always'
      },
      {
        type: 'advantage',
        target: 'save',
        operation: 'grant',
        value: 'all',
        condition: {
          when: 'vs_spell_cast_within_5ft',
          check: (character, context) => {
            return context?.spellCastWithin5ft === true;
          }
        }
      }
    ],
    passiveEffects: {
      concentrationDisadvantage: true,  // Keep for backward compatibility during migration
      advantageVsAdjacentSpells: true
    },
    activeAbilities: {
      opportunityAttackOnCast: {
        name: 'Spell Opportunity Attack',
        actionType: 'reaction',
        description: 'Attack when creature within 5ft casts a spell',
        trigger: 'adjacent_spellcast'
      }
    },
    tags: ['combat', 'anti-magic', 'reaction']
  },

  'martial_adept': {
    id: 'martial_adept',
    name: 'Martial Adept',
    description: 'You have martial training that allows you to perform special combat maneuvers.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'You learn two maneuvers of your choice from among those available to the Battle Master archetype.',
      'You gain one superiority die, which is a d6. This die is used to fuel your maneuvers.',
      'You regain your expended superiority dice when you finish a short or long rest.'
    ],
    // [PHASE 5.5 - NOV 18, 2025]: Added unified effects schema
    // This feat's functionality is in customChoices (maneuver selection) and activeAbilities (superiority die)
    effects: [],
    passiveEffects: null,
    activeAbilities: {
      superiorityDie: {
        name: 'Superiority Die',
        description: 'Fuel combat maneuvers',
        uses: 1,
        dieSize: 'd6',
        rechargeOn: 'shortRest'
      }
    },
    customChoices: {
      maneuvers: {
        choose: 2,
        from: 'battlemaster_maneuvers'
      }
    },
    tags: ['combat', 'tactical', 'versatile']
  },

  'medium_armor_master': {
    id: 'medium_armor_master',
    name: 'Medium Armor Master',
    description: 'You have practiced moving in medium armor to gain the following benefits.',
    source: 'PHB',
    prerequisites: {
      proficiencies: ['armor:medium']
    },
    benefits: [
      'Wearing medium armor doesn\'t impose disadvantage on your Dexterity (Stealth) checks.',
      'When you wear medium armor, you can add 3, rather than 2, to your AC if you have a Dexterity of 16 or higher.'
    ],
    // [PHASE 1.2 - NOV 14, 2025]: Added unified effects schema for EffectRegistry integration
    // [PHASE 4.2 - NOV 18, 2025]: Completed migration with remaining effects
    // Conditional effect: only applies when wearing medium armor
    effects: [
      {
        type: 'armorDexCap',
        operation: 'set',
        value: 3,
        condition: {
          when: 'wearingMediumArmor',
          // [Phase 4b-infra APR 2026]: Prefer context.wearingMediumArmor flag (set by
          // _calculateBaseAC) over peeking at character.equipment, which never matched the
          // real character shape (inventory[], not equipment[]). Falls back to context only
          // when no inventory was scanned.
          check: (character, context) => {
            if (context && typeof context.wearingMediumArmor === 'boolean') {
              return context.wearingMediumArmor;
            }
            // Legacy fallback: peek at character.equipment if context lacks the flag.
            const armor = character?.equipment?.find?.(item => item.equipSlot === EQUIPMENT_SLOTS.ARMOR);
            return armor?.properties?.armorType === 'medium';
          }
        }
      },
      {
        type: 'advantage',
        target: 'skill',
        operation: 'remove_disadvantage',
        value: 'stealth',
        condition: {
          when: 'wearing_medium_armor',
          check: (character, context) => {
            const armor = character.equipment?.find(item => item.equipSlot === EQUIPMENT_SLOTS.ARMOR);
            return armor?.properties?.armorType === 'medium';
          }
        }
      }
    ],
    passiveEffects: {
      noStealthDisadvantage: ['medium_armor'],  // Keep for backward compatibility during migration
      increasedDexCap: 3 // Keep for backward compatibility during migration
    },
    activeAbilities: null,
    tags: ['combat', 'defensive', 'stealth']
  },

  'mobile': {
    id: 'mobile',
    name: 'Mobile',
    description: 'You are exceptionally speedy and agile.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'Your speed increases by 10 feet.',
      'When you use the Dash action, difficult terrain doesn\'t cost you extra movement on that turn.',
      'When you make a melee attack against a creature, you don\'t provoke opportunity attacks from that creature for the rest of the turn, whether you hit or not.'
    ],
    // [PHASE 1.2 - NOV 14, 2025]: Added unified effects schema for EffectRegistry integration
    // [PHASE 4.1 - NOV 18, 2025]: Completed migration with remaining effects
    effects: [
      {
        type: 'speed',
        operation: 'add',
        value: 10,
        condition: 'always'
      },
      {
        type: 'feature',
        operation: 'grant',
        value: 'dash_ignores_difficult_terrain',
        condition: 'always'
      },
      {
        type: 'feature',
        operation: 'grant',
        value: 'no_opportunity_after_melee_attack',
        condition: 'always'
      }
    ],
    passiveEffects: {
      speed: 10, // Keep for backward compatibility during migration [PHASE 2.3 - NOV 11, 2025]: Fixed property name to match effectCalculator.calculateSpeed
      dashIgnoresDifficultTerrain: true,
      noOpportunityAfterAttack: true
    },
    activeAbilities: null,
    tags: ['combat', 'mobility', 'defensive']
  },

  'mounted_combatant': {
    id: 'mounted_combatant',
    name: 'Mounted Combatant',
    description: 'You are a dangerous foe to face while mounted.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'You have advantage on melee attack rolls against any unmounted creature that is smaller than your mount.',
      'You can force an attack targeted at your mount to target you instead.',
      'If your mount is subjected to an effect that allows it to make a Dexterity saving throw to take only half damage, it instead takes no damage if it succeeds on the saving throw, and only half damage if it fails.'
    ],
    // [PHASE 3.1 - NOV 18, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'advantage',
        target: 'melee_attack',
        operation: 'grant',
        value: true,
        condition: {
          when: 'mounted_vs_smaller',
          check: (character, context) => {
            // Check if mounted and target is smaller than mount
            return context?.mounted && context?.targetSmallerThanMount;
          }
        }
      },
      {
        type: 'feature',
        operation: 'grant',
        value: ['redirect_mount_attacks', 'mount_evasion'],
        condition: 'always'
      }
    ],
    passiveEffects: {
      mountedAdvantage: true, // Keep for backward compatibility during migration
      redirectAttacks: true,
      mountEvasion: true
    },
    activeAbilities: null,
    tags: ['combat', 'mounted', 'offensive']
  },

  'observant': {
    id: 'observant',
    name: 'Observant',
    description: 'Quick to notice details of your environment, you gain the following benefits.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'Increase your Intelligence or Wisdom score by 1, to a maximum of 20.',
      'If you can see a creature\'s mouth while it is speaking a language you understand, you can interpret what it\'s saying by reading its lips.',
      'You have a +5 bonus to your passive Wisdom (Perception) and passive Intelligence (Investigation) scores.'
    ],
    // [PHASE 1.2 - NOV 14, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'skill',
        target: 'perception',
        operation: 'add',
        value: 5,
        condition: 'always'
      },
      {
        type: 'skill',
        target: 'investigation',
        operation: 'add',
        value: 5,
        condition: 'always'
      }
    ],
    passiveEffects: {
      passivePerception: 5, // Keep for backward compatibility during migration
      passiveInvestigation: 5, // Keep for backward compatibility during migration
      lipReading: true
    },
    abilityScoreIncrease: {
      choice: [
        { int: 1 },
        { wis: 1 }
      ]
    },
    activeAbilities: null,
    tags: ['utility', 'perception', 'investigation']
  },

  'polearm_master': {
    id: 'polearm_master',
    name: 'Polearm Master',
    description: 'You gain the following benefits when using a quarterstaff, spear, glaive, halberd, pike, or lance.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'When you take the Attack action and attack with only a quarterstaff, spear, or polearm, you can use a bonus action to make a melee attack with the opposite end of the weapon (1d4 bludgeoning damage).',
      'While you are wielding a quarterstaff, spear, or polearm, other creatures provoke an opportunity attack from you when they enter the reach you have with that weapon.'
    ],
    // [PHASE 5.3 - NOV 18, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'feature',
        operation: 'grant',
        value: 'opportunity_attack_on_enter_reach',
        condition: {
          when: 'wielding_polearm',
          check: (character, context) => {
            // [Phase 4b-infra APR 2026]: Prefer context.wieldingPolearm (set by
            // EffectCalculator._buildContext). Legacy fallback reads
            // character.inventory — NOT character.equipment, which never
            // existed on real character data and silently dropped this feature
            // grant in production.
            if (context && typeof context.wieldingPolearm === 'boolean') return context.wieldingPolearm;
            // [PHASE B / #013 — STRING-SIN SWEEP]: Delegate to the shared
            //   isPolearm() helper so adding a polearm in one place updates
            //   both this feature condition and effectCalculator's
            //   wieldingPolearm context flag in a single spot.
            const inv = character.inventory || [];
            const weapon = inv.find(i =>
              i?.equipped &&
              (i?.equipSlot === EQUIPMENT_SLOTS.MAIN_HAND || i?.equipSlot === 'main_hand') &&
              isPolearm(i)
            );
            return Boolean(weapon);
          }
        }
      }
    ],
    passiveEffects: {
      reachOpportunityAttacks: true  // Keep for backward compatibility during migration
    },
    activeAbilities: {
      buttStrike: {
        name: 'Butt Strike',
        actionType: 'bonus',
        description: 'Attack with opposite end for 1d4 damage',
        damage: '1d4',
        damageType: 'bludgeoning',
        requirement: 'polearm'
      }
    },
    tags: ['combat', 'offensive', 'reach']
  },

  'resilient': {
    id: 'resilient',
    name: 'Resilient',
    description: 'Choose one ability score. You gain the following benefits.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'Increase the chosen ability score by 1, to a maximum of 20.',
      'You gain proficiency in saving throws using the chosen ability.'
    ],
    // [PHASE 5.1 - NOV 18, 2025]: Added unified effects schema
    // Save proficiency is granted dynamically via customChoices.ability.alsoGrants mechanism
    effects: [],
    customChoices: {
      ability: {
        choose: 1,
        from: ['str', 'dex', 'con', 'int', 'wis', 'cha'],
        effect: 'abilityScoreIncrease',
        alsoGrants: 'saveProficiency'
      }
    },
    tags: ['defensive', 'saves', 'ability_score']
  },

  'savage_attacker': {
    id: 'savage_attacker',
    name: 'Savage Attacker',
    description: 'Once per turn when you roll damage for a melee weapon attack, you can reroll the weapon\'s damage dice and use either total.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'Once per turn, reroll melee weapon damage and use either result.'
    ],
    // [PHASE 3.1 - NOV 18, 2025]: This feat's functionality is entirely in activeAbilities (active choice)
    // No passive effects to convert to unified schema
    effects: [],
    passiveEffects: null,
    activeAbilities: {
      savageDamage: {
        name: 'Savage Damage',
        description: 'Reroll melee damage once per turn',
        usesPerTurn: 1,
        actionType: 'free'
      }
    },
    tags: ['combat', 'offensive', 'damage']
  },

  'sentinel': {
    id: 'sentinel',
    name: 'Sentinel',
    description: 'You have mastered techniques to take advantage of every drop in any enemy\'s guard.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'When you hit a creature with an opportunity attack, the creature\'s speed becomes 0 for the rest of the turn.',
      'Creatures provoke opportunity attacks from you even if they take the Disengage action before leaving your reach.',
      'When a creature within 5 feet of you makes an attack against a target other than you (and that target doesn\'t have this feat), you can use your reaction to make a melee weapon attack against the attacking creature.'
    ],
    // [PHASE 5.3 - NOV 18, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'feature',
        operation: 'grant',
        value: 'stop_target_on_opportunity_hit',
        condition: 'always'
      },
      {
        type: 'feature',
        operation: 'grant',
        value: 'opportunity_attack_ignores_disengage',
        condition: 'always'
      }
    ],
    passiveEffects: {
      stopOnOpportunityHit: true,  // Keep for backward compatibility during migration
      ignoreDisengage: true
    },
    activeAbilities: {
      protectiveStrike: {
        name: 'Protective Strike',
        actionType: 'reaction',
        description: 'Attack creature that attacks ally within 5ft',
        trigger: 'ally_attacked_in_reach'
      }
    },
    tags: ['combat', 'defensive', 'control', 'reaction']
  },

  'sharpshooter': {
    id: 'sharpshooter',
    name: 'Sharpshooter',
    description: 'You have mastered ranged weapons and can make shots that others find impossible.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'Attacking at long range doesn\'t impose disadvantage on your ranged weapon attack rolls.',
      'Your ranged weapon attacks ignore half cover and three-quarters cover.',
      'Before you make an attack with a ranged weapon that you are proficient with, you can choose to take a -5 penalty to the attack roll. If the attack hits, you add +10 to the attack\'s damage.'
    ],
    // [PHASE 5.2 - NOV 18, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'combatAdvantage',
        target: 'ranged_long_range',
        operation: 'remove_disadvantage',
        value: true,
        condition: 'always'
      },
      {
        type: 'feature',
        operation: 'grant',
        value: 'ignore_half_cover',
        condition: 'always'
      },
      {
        type: 'feature',
        operation: 'grant',
        value: 'ignore_three_quarters_cover',
        condition: 'always'
      }
    ],
    passiveEffects: {
      noLongRangeDisadvantage: true,  // Keep for backward compatibility during migration
      ignoreCover: ['half', 'three-quarters']
    },
    toggleEffects: {
      powerShot: {
        name: 'Power Shot',
        description: '-5 to hit, +10 damage with ranged weapons',
        attackMod: -5,
        damageMod: 10,
        appliesTo: 'ranged'
      }
    },
    tags: ['combat', 'offensive', 'ranged', 'damage']
  },

  'shield_master': {
    id: 'shield_master',
    name: 'Shield Master',
    description: 'You use shields not just for protection but also for offense.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'If you take the Attack action on your turn, you can use a bonus action to try to shove a creature within 5 feet of you with your shield.',
      'If you aren\'t incapacitated, you can add your shield\'s AC bonus to any Dexterity saving throw you make against a spell or other harmful effect that targets only you.',
      'If you are subjected to an effect that allows you to make a Dexterity saving throw to take only half damage, you can use your reaction to take no damage if you succeed on the saving throw, interposing your shield between yourself and the source of the effect.'
    ],
    // [PHASE 4.1 - NOV 18, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'save',
        target: 'dexterity',
        operation: 'add',
        value: 'shield_ac_bonus',
        condition: {
          when: 'wielding_shield',
          check: (character, context) => {
            // [Phase 4b-infra APR 2026]: Prefer context.wieldingShield (set by
            // EffectCalculator._buildContext). Legacy fallback reads
            // character.inventory — NOT character.equipment, which never
            // existed on real character data and silently dropped this DEX-save
            // bonus in production.
            if (context && typeof context.wieldingShield === 'boolean') return context.wieldingShield;
            const inv = character.inventory || [];
            const shield = inv.find(item =>
              item?.equipped && (
                item?.equipSlot === EQUIPMENT_SLOTS.SHIELD ||
                (item?.equipSlot === EQUIPMENT_SLOTS.OFF_HAND &&
                 (item?.type === 'armor' || item?.category === 'armor') &&
                 (item?.armorType === 'shield' || item?.baseType === 'shield'))
              )
            );
            return Boolean(shield);
          }
        }
      }
    ],
    passiveEffects: {
      shieldBonusToDexSaves: true  // Keep for backward compatibility during migration
    },
    activeAbilities: {
      shieldBash: {
        name: 'Shield Bash',
        actionType: 'bonus',
        description: 'Shove with shield after Attack action',
        requirement: 'shield'
      },
      shieldEvasion: {
        name: 'Shield Evasion',
        actionType: 'reaction',
        description: 'Take no damage on successful DEX save',
        requirement: 'shield'
      }
    },
    tags: ['combat', 'defensive', 'control', 'shield']
  },

  'skilled': {
    id: 'skilled',
    name: 'Skilled',
    description: 'You gain proficiency in any combination of three skills or tools of your choice.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'Gain proficiency in 3 skills or tools of your choice.'
    ],
    // [PHASE 3.2 - NOV 18, 2025]: This feat's functionality is in customChoices (proficiency selection)
    // No passive effects to convert to unified schema - proficiencies are granted via customChoices system
    effects: [],
    customChoices: {
      proficiencies: {
        choose: 3,
        from: 'skills_and_tools'
      }
    },
    tags: ['utility', 'skills']
  },

  'skulker': {
    id: 'skulker',
    name: 'Skulker',
    description: 'You are expert at slinking through shadows.',
    source: 'PHB',
    prerequisites: {
      abilities: { dex: 13 }
    },
    benefits: [
      'You can try to hide when you are lightly obscured from the creature from which you are hiding.',
      'When you are hidden from a creature and miss it with a ranged weapon attack, making the attack doesn\'t reveal your position.',
      'Dim light doesn\'t impose disadvantage on your Wisdom (Perception) checks relying on sight.'
    ],
    // [PHASE 3.1 - NOV 18, 2025]: Added unified effects schema for EffectRegistry integration
    // [Phase 4b-infra APR 2026]: Consolidated orphaned types into canonical types.
    //   stealthAbility 'enable' → feature 'grant' (rule-flag, not a numeric stat)
    //   perceptionModifier 'remove_disadvantage' → advantage 'remove_disadvantage'
    //     (canonical advantage type — note: removing disadvantage is NOT granting advantage)
    effects: [
      {
        type: 'feature',
        operation: 'grant',
        value: 'hide_in_light_obscurement',
        condition: 'always'
      },
      {
        type: 'feature',
        operation: 'grant',
        value: 'ranged_miss_doesnt_reveal',
        condition: 'always'
      },
      {
        type: 'advantage',
        target: 'skill:perception',
        operation: 'remove_disadvantage',
        value: 'dim_light',  // descriptive context tag, not consumed by check yet
        condition: 'always'
      }
    ],
    passiveEffects: {
      hideInLightObscurement: true, // Keep for backward compatibility during migration
      missDoesntReveal: true,
      noDimLightDisadvantage: true
    },
    activeAbilities: null,
    tags: ['stealth', 'ranged', 'perception']
  },

  'tavern_brawler': {
    id: 'tavern_brawler',
    name: 'Tavern Brawler',
    description: 'Accustomed to rough-and-tumble fighting using whatever weapons happen to be at hand.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'Increase your Strength or Constitution score by 1, to a maximum of 20.',
      'You are proficient with improvised weapons.',
      'Your unarmed strike uses a d4 for damage.',
      'When you hit a creature with an unarmed strike or an improvised weapon on your turn, you can use a bonus action to attempt to grapple the target.'
    ],
    // [PHASE 1.2 - NOV 14, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'proficiency',
        target: 'weapon',
        value: 'improvised',
        operation: 'add',
        condition: 'always'
      },
      {
        type: 'unarmedDamage',
        operation: 'set',
        value: '1d4',
        condition: 'always'
      }
    ],
    passiveEffects: {
      improvisedProficiency: true, // Keep for backward compatibility
      unarmedDamage: 'd4' // Keep for backward compatibility during migration
    },
    abilityScoreIncrease: {
      choice: [
        { str: 1 },
        { con: 1 }
      ]
    },
    activeAbilities: {
      bonusGrapple: {
        name: 'Bonus Grapple',
        actionType: 'bonus',
        description: 'Grapple after unarmed or improvised attack',
        trigger: 'after_unarmed_or_improvised'
      }
    },
    tags: ['combat', 'grappling', 'unarmed']
  },

  'tough': {
    id: 'tough',
    name: 'Tough',
    description: 'Your hit point maximum increases by an amount equal to twice your level when you gain this feat. Whenever you gain a level thereafter, your hit point maximum increases by an additional 2 hit points.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'Gain 2 HP per character level.'
    ],
    // [PHASE 1.2 - NOV 14, 2025]: Added unified effects schema for EffectRegistry integration
    // Dynamic value: character level × 2
    effects: [
      {
        type: 'hp',
        operation: 'add',
        value: 'characterLevel*2', // Dynamic value evaluated by EffectRegistry
        condition: 'always'
      }
    ],
    passiveEffects: {
      hpPerLevel: 2 // Keep for backward compatibility during migration
    },
    activeAbilities: null,
    tags: ['defensive', 'hit_points']
  },

  'war_caster': {
    id: 'war_caster',
    name: 'War Caster',
    description: 'You have practiced casting spells in the midst of combat.',
    source: 'PHB',
    prerequisites: {
      spellcasting: true
    },
    benefits: [
      'You have advantage on Constitution saving throws that you make to maintain your concentration on a spell when you take damage.',
      'You can perform the somatic components of spells even when you have weapons or a shield in one or both hands.',
      'When a hostile creature\'s movement provokes an opportunity attack from you, you can use your reaction to cast a spell at the creature, rather than making an opportunity attack. The spell must have a casting time of 1 action and must target only that creature.'
    ],
    // [PHASE 5.4 - NOV 18, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'advantage',
        target: 'save',
        operation: 'grant',
        value: 'constitution',
        condition: {
          when: 'concentration_check',
          check: (character, context) => {
            return context?.concentrationCheck === true;
          }
        }
      },
      {
        type: 'feature',
        operation: 'grant',
        value: 'somatic_components_with_weapons_and_shield',
        condition: 'always'
      },
      // [FIX APR 2026 — F-1]: Register concentrationAdvantage with effectRegistry
      // [WHY]: passiveEffects are not registered by FeatsPanel — only effects[] entries are indexed
      // [VALUE]: Must be 1 (not true) because _getValue(true) returns 0 via parseInt(true)=NaN||0
      {
        type: 'concentrationAdvantage',
        operation: 'add',
        value: 1
      }
    ],
    // [#103 CLEANUP — 2026-05-18]: Deleted dead passiveEffects breadcrumb
    //   ({ concentrationAdvantage: true, somaticWithWeapons: true }). Both
    //   were duplicates of entries in effects[] above (concentrationAdvantage
    //   already registers via effectRegistry; the 'somatic with weapons' grant
    //   is the `feature` row at line 1005-1008). The convertLegacyEffect path
    //   in effectRegistry.js doesn't handle either key, so they were 100%
    //   inert. No runtime change.
    activeAbilities: {
      spellOpportunity: {
        name: 'Spell Opportunity Attack',
        actionType: 'reaction',
        description: 'Cast spell instead of opportunity attack',
        requirement: 'spell_with_1_action'
      }
    },
    tags: ['magic', 'combat', 'concentration']
  },

  // ========================================
  // MAGIC & SPELLCASTING FEATS
  // ========================================

  'elemental_adept': {
    id: 'elemental_adept',
    name: 'Elemental Adept',
    description: 'When you gain this feat, choose one damage type: acid, cold, fire, lightning, or thunder. Spells you cast ignore resistance to damage of the chosen type. In addition, when you roll damage for a spell you cast that deals damage of that type, you can treat any 1 on a damage die as a 2.',
    source: 'PHB',
    prerequisites: {
      spellcasting: true
    },
    benefits: [
      'Spells ignore resistance to chosen damage type.',
      'Treat 1s as 2s on damage dice for that type.'
    ],
    // [PHASE 5.5 - NOV 18, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'resistance',
        operation: 'ignore',
        value: 'chosen_element',
        condition: {
          when: 'spell_damage',
          check: (character, context) => {
            // [Phase 4b-infra APR 2026]: feats is an array of {id, customChoices, ...} stubs.
            // Old form `character.feats?.elemental_adept?.chosenElement` always evaluated to
            // undefined because it indexed an array by string id. Walk the array; accept the
            // chosenElement on the stub OR nested under customChoices.element.
            const taken = (character?.feats || []).find(f => f?.id === 'elemental_adept');
            const chosenElement = taken?.chosenElement || taken?.customChoices?.element;
            return context?.damageType === chosenElement && context?.isSpell === true;
          }
        }
      },
      {
        type: 'damage',
        operation: 'minimum',
        value: 2,
        condition: {
          when: 'chosen_element_spell',
          check: (character, context) => {
            // [Phase 4b-infra APR 2026]: see note above — same data-path fix.
            const taken = (character?.feats || []).find(f => f?.id === 'elemental_adept');
            const chosenElement = taken?.chosenElement || taken?.customChoices?.element;
            return context?.damageType === chosenElement && context?.isSpell === true;
          }
        }
      }
    ],
    customChoices: {
      element: {
        choose: 1,
        from: ['acid', 'cold', 'fire', 'lightning', 'thunder']
      }
    },
    passiveEffects: {
      ignoreResistance: 'chosen_element',  // Keep for backward compatibility during migration
      minDamageRoll: 2
    },
    tags: ['magic', 'damage', 'elemental']
  },

  'magic_initiate': {
    id: 'magic_initiate',
    name: 'Magic Initiate',
    description: 'Choose a class: bard, cleric, druid, sorcerer, warlock, or wizard. You learn two cantrips and one 1st-level spell from that class\'s spell list.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'Learn 2 cantrips and one 1st-level spell from chosen class.',
      'Can cast the 1st-level spell once per long rest.'
    ],
    // [PHASE 3.3 - NOV 18, 2025]: This feat's functionality is in customChoices (spell learning) and activeAbilities (spell casting)
    // No passive effects to convert to unified schema
    effects: [],
    customChoices: {
      class: {
        choose: 1,
        from: ['bard', 'cleric', 'druid', 'sorcerer', 'warlock', 'wizard']
      },
      cantrips: {
        choose: 2,
        from: 'class_cantrips'
      },
      spell: {
        choose: 1,
        from: 'class_1st_level_spells'
      }
    },
    activeAbilities: {
      initiateSpell: {
        name: 'Magic Initiate Spell',
        description: 'Cast chosen 1st-level spell',
        uses: 1,
        rechargeOn: 'longRest'
      }
    },
    tags: ['magic', 'spellcasting', 'versatile']
  },

  'ritual_caster': {
    id: 'ritual_caster',
    name: 'Ritual Caster',
    description: 'You have learned a number of spells that you can cast as rituals.',
    source: 'PHB',
    prerequisites: {
      abilities: {
        choice: [
          { int: 13 },
          { wis: 13 }
        ]
      }
    },
    benefits: [
      'Acquire a ritual book with two 1st-level ritual spells.',
      'Can cast known ritual spells as rituals.',
      'Can copy new ritual spells into your book.'
    ],
    // [PHASE 3.3 - NOV 18, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'feature',
        operation: 'grant',
        value: 'ritual_casting',
        condition: 'always'
      },
      {
        type: 'feature',
        operation: 'grant',
        value: 'copy_ritual_spells',
        condition: 'always'
      }
    ],
    customChoices: {
      class: {
        choose: 1,
        from: ['bard', 'cleric', 'druid', 'sorcerer', 'warlock', 'wizard']
      },
      ritualSpells: {
        choose: 2,
        from: 'class_ritual_spells'
      }
    },
    passiveEffects: {
      ritualCasting: true  // Keep for backward compatibility during migration
    },
    tags: ['magic', 'ritual', 'utility']
  },

  'spell_sniper': {
    id: 'spell_sniper',
    name: 'Spell Sniper',
    description: 'You have learned techniques to enhance your attacks with certain kinds of spells.',
    source: 'PHB',
    prerequisites: {
      spellcasting: true
    },
    benefits: [
      'When you cast a spell that requires you to make an attack roll, the spell\'s range is doubled.',
      'Your ranged spell attacks ignore half cover and three-quarters cover.',
      'You learn one cantrip that requires an attack roll from any class spell list.'
    ],
    // [PHASE 5.5 - NOV 18, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'feature',
        operation: 'grant',
        value: 'double_spell_range_for_attack_spells',
        condition: 'always'
      },
      {
        type: 'feature',
        operation: 'grant',
        value: 'spell_attacks_ignore_half_cover',
        condition: 'always'
      },
      {
        type: 'feature',
        operation: 'grant',
        value: 'spell_attacks_ignore_three_quarters_cover',
        condition: 'always'
      }
    ],
    passiveEffects: {
      doubleSpellRange: true,  // Keep for backward compatibility during migration
      spellsIgnoreCover: ['half', 'three-quarters']
    },
    customChoices: {
      cantrip: {
        choose: 1,
        from: 'attack_cantrips'
      }
    },
    tags: ['magic', 'ranged', 'offensive']
  },

  // ========================================
  // SKILL & UTILITY FEATS
  // ========================================

  'actor': {
    id: 'actor',
    name: 'Actor',
    description: 'Skilled at mimicry and dramatics.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'Increase your Charisma score by 1, to a maximum of 20.',
      'You have advantage on Charisma (Deception) and Charisma (Performance) checks when trying to pass yourself off as a different person.',
      'You can mimic the speech of another person or the sounds made by other creatures.'
    ],
    abilityScoreIncrease: {
      cha: 1
    },
    // [PHASE 3.2 - NOV 18, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'advantage',
        target: 'skill',
        operation: 'grant',
        value: 'deception',
        condition: {
          when: 'disguised_as_different_person',
          check: (character, context) => {
            return context?.disguised === true;
          }
        }
      },
      {
        type: 'advantage',
        target: 'skill',
        operation: 'grant',
        value: 'performance',
        condition: {
          when: 'disguised_as_different_person',
          check: (character, context) => {
            return context?.disguised === true;
          }
        }
      },
      {
        type: 'feature',
        operation: 'grant',
        value: 'mimicry',
        condition: 'always'
      }
    ],
    passiveEffects: {
      disguiseAdvantage: true,  // Keep for backward compatibility during migration
      mimicry: true
    },
    tags: ['social', 'deception', 'performance']
  },

  'athlete': {
    id: 'athlete',
    name: 'Athlete',
    description: 'You have undergone extensive physical training.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'Increase your Strength or Dexterity score by 1, to a maximum of 20.',
      'When you are prone, standing up uses only 5 feet of your movement.',
      'Climbing doesn\'t cost you extra movement.',
      'You can make a running long jump or a running high jump after moving only 5 feet on foot.'
    ],
    abilityScoreIncrease: {
      choice: [
        { str: 1 },
        { dex: 1 }
      ]
    },
    // [PHASE 3.2 - NOV 18, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'feature',
        operation: 'grant',
        value: 'stand_from_prone_5ft',
        condition: 'always'
      },
      {
        type: 'speed',
        target: 'climb',
        operation: 'set',
        value: 'equal_to_walk',
        condition: 'always'
      },
      {
        type: 'feature',
        operation: 'grant',
        value: 'running_jump_5ft',
        condition: 'always'
      }
    ],
    passiveEffects: {
      standUpBonus: true,  // Keep for backward compatibility during migration
      climbSpeed: true,
      improvedJump: true
    },
    tags: ['mobility', 'athletics']
  },

  'charger': {
    id: 'charger',
    name: 'Charger',
    description: 'When you use your action to Dash, you can use a bonus action to make one melee weapon attack or to shove a creature.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'After Dash action, can make bonus action attack with +5 damage or shove with 10ft knockback.'
    ],
    // [PHASE 3.1 - NOV 18, 2025]: This feat's functionality is entirely in activeAbilities (bonus action after Dash)
    // No passive effects to convert to unified schema
    effects: [],
    activeAbilities: {
      chargingAttack: {
        name: 'Charging Attack',
        actionType: 'bonus',
        description: 'Attack with +5 damage or shove 10ft after Dash',
        damageMod: 5,
        trigger: 'after_dash'
      }
    },
    tags: ['combat', 'mobility', 'offensive']
  },

  'dungeon_delver': {
    id: 'dungeon_delver',
    name: 'Dungeon Delver',
    description: 'Alert to the hidden traps and secret doors found in many dungeons.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'You have advantage on Wisdom (Perception) and Intelligence (Investigation) checks made to detect the presence of secret doors.',
      'You have advantage on saving throws made to avoid or resist traps.',
      'You have resistance to damage dealt by traps.',
      'Traveling at a fast pace doesn\'t impose the normal -5 penalty on your passive Wisdom (Perception) score.'
    ],
    // [PHASE 3.3 - NOV 18, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'advantage',
        target: 'skill',
        operation: 'grant',
        value: 'perception',
        condition: {
          when: 'detecting_secret_doors',
          check: (character, context) => {
            return context?.detectingSecretDoors === true;
          }
        }
      },
      {
        type: 'advantage',
        target: 'skill',
        operation: 'grant',
        value: 'investigation',
        condition: {
          when: 'detecting_secret_doors',
          check: (character, context) => {
            return context?.detectingSecretDoors === true;
          }
        }
      },
      {
        type: 'advantage',
        target: 'save',
        operation: 'grant',
        value: 'all',
        condition: {
          when: 'against_traps',
          check: (character, context) => {
            return context?.trapSave === true;
          }
        }
      },
      {
        type: 'resistance',
        operation: 'grant',
        value: 'trap_damage',
        condition: 'always'
      },
      {
        type: 'feature',
        operation: 'grant',
        value: 'no_fast_pace_perception_penalty',
        condition: 'always'
      }
    ],
    passiveEffects: {
      secretDoorAdvantage: true,  // Keep for backward compatibility during migration
      trapSaveAdvantage: true,
      trapResistance: true,
      noFastPacePerceptionPenalty: true
    },
    tags: ['exploration', 'perception', 'defensive']
  },

  'durable': {
    id: 'durable',
    name: 'Durable',
    description: 'Hardy and resilient.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'Increase your Constitution score by 1, to a maximum of 20.',
      'When you roll a Hit Die to regain hit points, the minimum number of hit points you regain equals twice your Constitution modifier (minimum of 2).'
    ],
    abilityScoreIncrease: {
      con: 1
    },
    // [PHASE 1.2 - NOV 14, 2025]: Added unified effects schema for EffectRegistry integration
    // Dynamic effect: minimum hit die recovery = 2 × CON modifier (minimum 2)
    effects: [
      {
        type: 'hitDieRecovery',
        operation: 'minimum',
        value: 'max(2*constitutionModifier, 2)', // Dynamic value: max of (2×CON mod, 2)
        condition: 'always'
      }
    ],
    passiveEffects: {
      minHitDieRecovery: '2*con_mod' // Keep for backward compatibility during migration
    },
    tags: ['defensive', 'recovery', 'constitution']
  },

  'grappler': {
    id: 'grappler',
    name: 'Grappler',
    description: 'You\'ve developed the skills necessary to hold your own in close-quarters grappling.',
    source: 'PHB',
    prerequisites: {
      abilities: { str: 13 }
    },
    benefits: [
      'You have advantage on attack rolls against a creature you are grappling.',
      'You can use your action to try to pin a creature grappled by you.'
    ],
    // [PHASE 3.1 - NOV 18, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'advantage',
        target: 'attack',
        operation: 'grant',
        value: true,
        condition: {
          when: 'grappling_target',
          check: (character, context) => {
            return context?.grapplingTarget === true;
          }
        }
      }
    ],
    passiveEffects: {
      grappleAdvantage: true  // Keep for backward compatibility during migration
    },
    activeAbilities: {
      pin: {
        name: 'Pin',
        actionType: 'action',
        description: 'Pin a grappled creature',
        requirement: 'target_grappled'
      }
    },
    tags: ['combat', 'grappling', 'control']
  },

  'heavily_armored': {
    id: 'heavily_armored',
    name: 'Heavily Armored',
    description: 'You have trained to master the use of heavy armor.',
    source: 'PHB',
    prerequisites: {
      proficiencies: ['armor:medium']
    },
    benefits: [
      'Increase your Strength score by 1, to a maximum of 20.',
      'You gain proficiency with heavy armor.'
    ],
    abilityScoreIncrease: {
      str: 1
    },
    // [PHASE 1.2 - NOV 14, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'proficiency',
        target: 'armor',
        value: 'heavy',
        operation: 'add',
        condition: 'always'
      }
    ],
    passiveEffects: {
      proficiencies: ['armor:heavy'] // Keep for backward compatibility during migration
    },
    tags: ['combat', 'defensive', 'armor']
  },

  'heavy_armor_master': {
    id: 'heavy_armor_master',
    name: 'Heavy Armor Master',
    description: 'You can use your armor to deflect strikes that would kill others.',
    source: 'PHB',
    prerequisites: {
      proficiencies: ['armor:heavy']
    },
    benefits: [
      'Increase your Strength score by 1, to a maximum of 20.',
      'While wearing heavy armor, reduce bludgeoning, piercing, and slashing damage from nonmagical attacks by 3.'
    ],
    abilityScoreIncrease: {
      str: 1
    },
    // [PHASE 1.2 - NOV 14, 2025]: Added unified effects schema for EffectRegistry integration
    // Complex effect: damage reduction with multiple conditions
    effects: [
      {
        type: 'damageReduction',
        operation: 'subtract',
        value: 3,
        target: ['bludgeoning', 'piercing', 'slashing'],
        condition: {
          when: 'wearingHeavyArmor_and_nonmagicalDamage',
          check: (character, context) => {
            // [Phase 4b-infra APR 2026]: Prefer context.wearingHeavyArmor (set by
            // EffectCalculator._buildContext). Legacy fallback reads
            // character.inventory — NOT character.equipment, which never
            // existed on real character data and silently dropped this damage
            // reduction in production.
            const isWearingHeavy = (context && typeof context.wearingHeavyArmor === 'boolean')
              ? context.wearingHeavyArmor
              : (() => {
                  const armor = (character.inventory || []).find(item =>
                    item?.equipped && item?.equipSlot === EQUIPMENT_SLOTS.ARMOR && item?.category === 'armor'
                  );
                  return armor?.armorType === 'heavy';
                })();
            if (!isWearingHeavy) return false;

            // Check if damage is nonmagical (context should be provided during damage calculation)
            const isNonmagical = context?.damage?.source !== 'magical';

            // Check if damage type matches
            const damageType = context?.damage?.type;
            const isCorrectType = ['bludgeoning', 'piercing', 'slashing'].includes(damageType);

            return isNonmagical && isCorrectType;
          }
        }
      }
    ],
    passiveEffects: {
      damageReduction: {
        amount: 3,
        types: ['bludgeoning', 'piercing', 'slashing'],
        condition: 'nonmagical',
        requirement: 'heavy_armor'
      } // Keep for backward compatibility during migration
    },
    tags: ['combat', 'defensive', 'armor', 'damage_reduction']
  },

  'keen_mind': {
    id: 'keen_mind',
    name: 'Keen Mind',
    description: 'You have a mind that can track time, direction, and detail with uncanny precision.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'Increase your Intelligence score by 1, to a maximum of 20.',
      'You always know which way is north.',
      'You always know the number of hours left before the next sunrise or sunset.',
      'You can accurately recall anything you have seen or heard within the past month.'
    ],
    abilityScoreIncrease: {
      int: 1
    },
    // [PHASE 3.2 - NOV 18, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'feature',
        operation: 'grant',
        value: 'perfect_direction',
        condition: 'always'
      },
      {
        type: 'feature',
        operation: 'grant',
        value: 'timekeeping',
        condition: 'always'
      },
      {
        type: 'feature',
        operation: 'grant',
        value: 'perfect_memory_1_month',
        condition: 'always'
      }
    ],
    passiveEffects: {
      perfectDirection: true,  // Keep for backward compatibility during migration
      timeKeeping: true,
      perfectMemory: '1 month'
    },
    tags: ['utility', 'intelligence', 'memory']
  },

  'lightly_armored': {
    id: 'lightly_armored',
    name: 'Lightly Armored',
    description: 'You have trained to master the use of light armor.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'Increase your Strength or Dexterity score by 1, to a maximum of 20.',
      'You gain proficiency with light armor.'
    ],
    abilityScoreIncrease: {
      choice: [
        { str: 1 },
        { dex: 1 }
      ]
    },
    // [PHASE 1.2 - NOV 14, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'proficiency',
        target: 'armor',
        value: 'light',
        operation: 'add',
        condition: 'always'
      }
    ],
    passiveEffects: {
      proficiencies: ['armor:light'] // Keep for backward compatibility during migration
    },
    tags: ['combat', 'defensive', 'armor']
  },

  'linguist': {
    id: 'linguist',
    name: 'Linguist',
    description: 'You have studied languages and codes.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'Increase your Intelligence score by 1, to a maximum of 20.',
      'You learn three languages of your choice.',
      'You can create written ciphers that can only be understood by those you teach or those with Intelligence (Investigation) DC equal to your Intelligence score + proficiency bonus.'
    ],
    abilityScoreIncrease: {
      int: 1
    },
    customChoices: {
      languages: {
        choose: 3,
        from: 'all_languages'
      }
    },
    // [PHASE 3.2 - NOV 18, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'feature',
        operation: 'grant',
        value: 'create_ciphers',
        condition: 'always'
      }
    ],
    passiveEffects: {
      createCiphers: true  // Keep for backward compatibility during migration
    },
    tags: ['utility', 'intelligence', 'languages']
  },

  'moderately_armored': {
    id: 'moderately_armored',
    name: 'Moderately Armored',
    description: 'You have trained to master the use of medium armor and shields.',
    source: 'PHB',
    prerequisites: {
      proficiencies: ['armor:light']
    },
    benefits: [
      'Increase your Strength or Dexterity score by 1, to a maximum of 20.',
      'You gain proficiency with medium armor and shields.'
    ],
    abilityScoreIncrease: {
      choice: [
        { str: 1 },
        { dex: 1 }
      ]
    },
    // [PHASE 1.2 - NOV 14, 2025]: Added unified effects schema for EffectRegistry integration
    effects: [
      {
        type: 'proficiency',
        target: 'armor',
        value: 'medium',
        operation: 'add',
        condition: 'always'
      },
      {
        type: 'proficiency',
        target: 'equipment',
        value: 'shields',
        operation: 'add',
        condition: 'always'
      }
    ],
    passiveEffects: {
      proficiencies: ['armor:medium', 'shields'] // Keep for backward compatibility during migration
    },
    tags: ['combat', 'defensive', 'armor']
  },

  'weapon_master': {
    id: 'weapon_master',
    name: 'Weapon Master',
    description: 'You have practiced extensively with a variety of weapons.',
    source: 'PHB',
    prerequisites: null,
    benefits: [
      'Increase your Strength or Dexterity score by 1, to a maximum of 20.',
      'You gain proficiency with four simple or martial weapons of your choice.'
    ],
    // [PHASE 5.1 - NOV 18, 2025]: Added unified effects schema
    // Weapon proficiencies are granted dynamically via customChoices.weapons mechanism
    effects: [],
    abilityScoreIncrease: {
      choice: [
        { str: 1 },
        { dex: 1 }
      ]
    },
    customChoices: {
      weapons: {
        choose: 4,
        from: 'all_weapons'
      }
    },
    tags: ['combat', 'weapons', 'versatile']
  }
};

// Helper function to get feat by ID
export function getFeatById(featId) {
  return SRD_FEATS[featId] || null;
}

// Helper function to check if character meets prerequisites
// [PHASE 1.1 - NOV 18, 2025]: Enhanced to support race, class, level prerequisites for source book feats
export function canTakeFeat(feat, character) {
  if (!feat.prerequisites) return true;

  const prereqs = feat.prerequisites;

  // Check ability score requirements
  if (prereqs.abilities) {
    if (prereqs.abilities.choice) {
      // Need to meet at least one of the choices
      const meetsOne = prereqs.abilities.choice.some(req => {
        return Object.entries(req).every(([stat, value]) =>
          (character[stat] || 10) >= value
        );
      });
      if (!meetsOne) return false;
    } else {
      // Need to meet all requirements
      const meetsAll = Object.entries(prereqs.abilities).every(([stat, value]) =>
        (character[stat] || 10) >= value
      );
      if (!meetsAll) return false;
    }
  }

  // [PHASE 1.1]: Check race requirements (for racial feats like Dragon Fear, Elven Accuracy)
  if (prereqs.race) {
    // [v1.0]: Use canonical path first, fall back to deprecated for migration compatibility
    const charRace = (character.basics?.race || character.race || '').toLowerCase();
    if (Array.isArray(prereqs.race)) {
      // Multiple valid races (e.g., "Elf or half-elf")
      const matchesRace = prereqs.race.some(race =>
        charRace.includes(race.toLowerCase())
      );
      if (!matchesRace) return false;
    } else {
      // Single race requirement
      if (!charRace.includes(prereqs.race.toLowerCase())) return false;
    }
  }

  // [PHASE 1.1]: Check class requirements (for class-specific feats)
  if (prereqs.class) {
    const charClasses = character.classes?.map(c => c.className.toLowerCase()) ||
                       [(character.class || '').toLowerCase()];
    if (Array.isArray(prereqs.class)) {
      // Multiple valid classes
      const hasClass = prereqs.class.some(cls =>
        charClasses.includes(cls.toLowerCase())
      );
      if (!hasClass) return false;
    } else {
      // Single class requirement
      if (!charClasses.includes(prereqs.class.toLowerCase())) return false;
    }
  }

  // [PHASE 1.1]: Check level requirements (for high-level feats)
  if (prereqs.level) {
    const charLevel = character.level ||
                     (character.classes?.reduce((sum, c) => sum + c.level, 0)) || 1;
    if (charLevel < prereqs.level) return false;
  }

  // [SUB-PLAN 1B-v — 2026-05-23]: New structured prereq fields written by the
  //   CustomFeatCreator reference-picker UI (b4c641f shipped the picker;
  //   commit body explicitly logged the runtime gate as a follow-up).
  // [SEMANTICS] (per b4c641f commit body):
  //   - requiredClasses/Races/Feats are arrays of ids/names.
  //   - Multi-entry within a list = OR (any-of).
  //   - Cross-list = AND (each non-empty list must be satisfied).
  //   - Empty array = no constraint (UI may leave a row blank).
  //   - Coexists additively with legacy prereqs.class / prereqs.race / etc.
  // [MATCH RULES]:
  //   - requiredClasses: exact list-includes against character.classes[].className (lowercased)
  //   - requiredRaces: substring match against character.basics.race (parity with legacy prereqs.race for subrace compat — e.g., 'Hill Dwarf' satisfies 'Dwarf')
  //   - requiredFeats: exact id match against character.feats[].id
  if (Array.isArray(prereqs.requiredClasses) && prereqs.requiredClasses.length > 0) {
    const charClasses = character.classes?.map(c => (c.className || '').toLowerCase()) ||
                       [(character.class || '').toLowerCase()];
    const hasOne = prereqs.requiredClasses.some(cls =>
      charClasses.includes(String(cls || '').toLowerCase())
    );
    if (!hasOne) return false;
  }

  if (Array.isArray(prereqs.requiredRaces) && prereqs.requiredRaces.length > 0) {
    const charRace = (character.basics?.race || character.race || '').toLowerCase();
    const hasOne = prereqs.requiredRaces.some(race =>
      charRace.includes(String(race || '').toLowerCase())
    );
    if (!hasOne) return false;
  }

  if (Array.isArray(prereqs.requiredFeats) && prereqs.requiredFeats.length > 0) {
    const charFeatIds = (character.feats || []).map(f => f && f.id).filter(Boolean);
    const hasOne = prereqs.requiredFeats.some(featId => charFeatIds.includes(featId));
    if (!hasOne) return false;
  }

  // Check proficiency requirements
  // [FIX JAN 12, 2026]: Fixed TypeError when character.proficiencies[type] is not an array
  // [ROOT CAUSE]: character.proficiencies is an OBJECT with array values (e.g., {armor: ['Light', 'Medium']})
  // [BUG]: Line 1774 assumed it was always an array, line 1776 wrongly called .includes() on the object
  if (prereqs.proficiencies) {
    const hasProficiencies = prereqs.proficiencies.every(prof => {
      if (prof.includes(':')) {
        // Format: "type:specific" e.g., "armor:Heavy"
        const [type, specific] = prof.split(':');
        const profList = character.proficiencies?.[type];
        // [FIX]: Check if profList is an array before calling .includes()
        if (!Array.isArray(profList)) return false;
        // Case-insensitive match for flexibility
        return profList.some(p =>
          typeof p === 'string' && p.toLowerCase().includes(specific.toLowerCase())
        );
      }
      // Simple proficiency name - search across all proficiency categories
      // [FIX]: character.proficiencies is an object, not an array
      if (!character.proficiencies || typeof character.proficiencies !== 'object') return false;
      return Object.values(character.proficiencies).some(profArray =>
        Array.isArray(profArray) && profArray.some(p =>
          typeof p === 'string' && p.toLowerCase().includes(prof.toLowerCase())
        )
      );
    });
    if (!hasProficiencies) return false;
  }

  // [FIX APR 2026 — F-9]: Data-driven spellcasting prerequisite check
  // [WHY]: Custom spellcasting classes from Content Workshop can now take spellcasting feats
  if (prereqs.spellcasting) {
    const { getCasterType } = require('../utils/contentResolver');
    const hasSpellcasting = character.classes?.some(c => getCasterType(c.className) !== null) ||
      (character.class && getCasterType(character.class) !== null);
    if (!hasSpellcasting) return false;
  }

  return true;
}

// [PHASE 1.2 - NOV 18, 2025]: Helper function to convert text prerequisites to structured objects
// PURPOSE: Convert source book feats from "Halfling" text format to {race: 'halfling'} object format
// WHY: Enables proper prerequisite validation for all 35 source book feats
export function convertTextPrerequisiteToStructured(text) {
  if (!text || text === "None" || text === "none") {
    return null;
  }

  // Convert to lowercase for matching
  const lower = text.toLowerCase();

  // Race prerequisites
  const racePatterns = [
    { match: /halfling/, value: { race: 'halfling' } },
    { match: /dragonborn/, value: { race: 'dragonborn' } },
    { match: /elf.*half-elf|half-elf.*elf/, value: { race: ['elf', 'half-elf'] } },
    { match: /elf.*drow|drow/, value: { race: 'elf' } }, // Drow are elves
    { match: /elf/, value: { race: 'elf' } },
    { match: /dwarf/, value: { race: 'dwarf' } },
    { match: /gnome/, value: { race: 'gnome' } },
    { match: /half-orc|orc/, value: { race: 'half-orc' } },
    { match: /tiefling/, value: { race: 'tiefling' } }
  ];

  for (const pattern of racePatterns) {
    if (pattern.match.test(lower)) {
      return pattern.value;
    }
  }

  // Spellcasting prerequisites
  if (lower.includes('spellcasting') || lower.includes('pact magic')) {
    return { spellcasting: true };
  }

  // Proficiency prerequisites
  if (lower.includes('proficiency')) {
    if (lower.includes('martial weapon')) {
      return { proficiencies: ['weapon:martial'] };
    }
    if (lower.includes('medium armor')) {
      return { proficiencies: ['armor:medium'] };
    }
    if (lower.includes('heavy armor')) {
      return { proficiencies: ['armor:heavy'] };
    }
  }

  // Ability score prerequisites
  const abilityMatch = lower.match(/(str|dex|con|int|wis|cha).*?(\d+)/);
  if (abilityMatch) {
    const [, ability, value] = abilityMatch;
    return { abilities: { [ability]: parseInt(value) } };
  }

  // Level prerequisites
  const levelMatch = lower.match(/(\d+).*?level/);
  if (levelMatch) {
    return { level: parseInt(levelMatch[1]) };
  }

  // If we can't convert, return the text with a warning
  console.warn(`[convertTextPrerequisiteToStructured] Could not convert: "${text}"`);
  return null;
}

// Helper function to apply passive feat effects
export function applyFeatEffects(feats, character) {
  const effects = {
    stats: {},
    bonuses: {},
    proficiencies: [],
    features: []
  };

  feats.forEach(featData => {
    const feat = getFeatById(featData.id);
    if (!feat) return;

    // Apply ability score increases
    if (feat.abilityScoreIncrease) {
      if (feat.abilityScoreIncrease.choice) {
        // Player has chosen which ability to increase
        const choice = featData.chosenAbility;
        if (choice) {
          Object.entries(choice).forEach(([stat, value]) => {
            effects.stats[stat] = (effects.stats[stat] || 0) + value;
          });
        }
      } else {
        // Fixed ability increase
        Object.entries(feat.abilityScoreIncrease).forEach(([stat, value]) => {
          effects.stats[stat] = (effects.stats[stat] || 0) + value;
        });
      }
    }

    // Apply passive effects
    if (feat.passiveEffects) {
      Object.entries(feat.passiveEffects).forEach(([key, value]) => {
        effects.bonuses[key] = value;
      });
    }

    // Add granted proficiencies
    if (feat.passiveEffects?.proficiencies) {
      effects.proficiencies.push(...feat.passiveEffects.proficiencies);
    }

    // Add features
    effects.features.push({
      name: feat.name,
      source: 'feat',
      description: feat.description
    });
  });

  return effects;
}

// Helper function to get all active feat abilities
export function getActiveFeatAbilities(feats) {
  const abilities = [];

  feats.forEach(featData => {
    const feat = getFeatById(featData.id);
    if (!feat || !feat.activeAbilities) return;

    Object.entries(feat.activeAbilities).forEach(([key, ability]) => {
      abilities.push({
        ...ability,
        featName: feat.name,
        featId: feat.id,
        abilityKey: key,
        currentUses: featData.activeUses?.[key] || ability.uses || 0
      });
    });
  });

  return abilities;
}

// Helper function to get toggle-able feat effects
export function getFeatToggles(feats) {
  const toggles = [];

  feats.forEach(featData => {
    const feat = getFeatById(featData.id);
    if (!feat || !feat.toggleEffects) return;

    Object.entries(feat.toggleEffects).forEach(([key, toggle]) => {
      toggles.push({
        ...toggle,
        featName: feat.name,
        featId: feat.id,
        toggleKey: key,
        active: featData.toggles?.[key] || false
      });
    });
  });

  return toggles;
}

// Export feat tags for filtering
export const FEAT_TAGS = [
  'combat',
  'defensive',
  'offensive',
  'magic',
  'utility',
  'skills',
  'mobility',
  'social',
  'perception',
  'stealth',
  'grappling',
  'ranged',
  'damage',
  'healing',
  'armor',
  'weapons',
  'saves',
  'concentration',
  'ritual',
  'ability_score'
];

// Helper Functions

// Get feat data by ID
//
// [P1.1 — APR 2026]: Custom-feat fallback. When the user picks a custom
// feat (id: `custom_<timestamp>`) from the Add Feat dialog, addFeat must
// resolve the feat to read its effects, choices, and prerequisites. The
// SRD_FEATS lookup misses for custom ids — passing the character's
// `playerStats.customFeats` array as the second arg lets the resolver
// consult that source as a fallback. SRD always wins (defense in depth)
// so a colliding custom id can never shadow an SRD entry.
//
// Mirrors contentResolver.js's SRD-first-then-custom shape for classes
// and races. The second arg is optional so existing call sites that do
// not have a character in scope (legacy paths, debug consoles) keep
// working with SRD-only behavior.
export const getFeatData = (featId, customFeats = null) => {
  // [PRIORITY 1]: SRD feats (always available, immutable; defense in depth
  // against a colliding source-book or custom id).
  if (SRD_FEATS[featId]) return SRD_FEATS[featId];

  // [PRIORITY 2 — BATCH 3 / P0 C-2 — APR 2026]: Source-book feats from
  // enabled sourcebooks (Tasha's Crusher/Slasher/Piercer, Xanathar's,
  // Fizban's). Pre-fix, these feats were INVISIBLE here — players could
  // pick them in the Add Feat picker (which queries getFeatsFromSources
  // directly), but every effectCalculator / FeatsPanel / RestPanel /
  // useFeatEffects call back into getFeatData(featId, customFeats) returned
  // null, silently dropping every passive bonus and effect.
  //
  // Mirrors contentResolver.getClassData / getRaceData — internal lazy
  // import keeps the dependency one-way (srdFeats → sourcesManager) so
  // all 14+ call sites benefit transparently with no signature change.
  // Lazy require avoids a circular at module-load (sourcesManager
  // ultimately re-imports srdFeats via initializeSources).
  try {
    // eslint-disable-next-line global-require
    const { sourcesManager } = require('./sourcesManager');
    const sourceFeats = sourcesManager.getAllFeats();
    if (sourceFeats && sourceFeats[featId]) return sourceFeats[featId];
  } catch (_e) {
    // sourcesManager unavailable in some debug/CLI paths — fall through.
  }

  // [PRIORITY 3]: Per-character custom feats (Content Workshop output).
  if (Array.isArray(customFeats)) {
    return customFeats.find(f => f && f.id === featId) || null;
  }
  return null;
};

// Check if character meets feat prerequisites
export const checkPrerequisites = (feat, character) => {
  if (!feat.prerequisites || feat.prerequisites.length === 0) {
    return true;
  }

  return feat.prerequisites.every(prereq => {
    if (typeof prereq === 'string') {
      // Simple string prerequisites (e.g., "Elf or Half-Elf")
      return true; // Would need more complex parsing for racial requirements
    }

    switch (prereq.type) {
      case 'ability':
        const score = character[prereq.ability?.toLowerCase()] || 10;
        return score >= (prereq.value || 13);

      case 'level':
        return (character.level || 1) >= (prereq.value || 1);

      case 'proficiency':
        const proficiencies = character.proficiencies || {};
        const profType = prereq.proficiency?.toLowerCase();

        if (profType?.includes('armor')) {
          return proficiencies.armor?.includes(prereq.proficiency);
        }
        if (profType?.includes('weapon')) {
          return proficiencies.weapons?.includes(prereq.proficiency);
        }
        return false;

      case 'feature':
        // [FIX JAN 2026]: Handle both string and object formats for features
        return character.features?.some(f =>
          (typeof f === 'string' ? f : f.name) === prereq.feature
        );

      case 'spellcasting':
        // [v1.0]: Property-based spellcaster detection (no hardcoded class names)
        if (character.knownSpells?.length > 0 || character.preparedSpells?.length > 0) return true;
        // Check if any of the character's classes are spellcasters via contentResolver
        try {
          const { isSpellcaster } = require('../utils/contentResolver');
          const className = character.classes?.[0]?.className || character.class;
          if (className && isSpellcaster(className)) return true;
          // Also check multiclass entries
          if (character.classes?.some(c => isSpellcaster(c.className))) return true;
        } catch (e) { /* contentResolver not available */ }
        return false;

      default:
        return true;
    }
  });
};

// [PURPOSE]: Apply passive feat effects to character stats
// [WHY]: Feats provide bonuses to AC, initiative, speed, ability scores, etc.
// [FIX 2025-10-12]: Handle OBJECT structure (not just ARRAY) for passiveEffects
// [INTEGRATION]: Called by EquipmentPanel for AC calculation, used throughout character system
export const applyPassiveEffects = (feats, character) => {
  const effects = {
    abilityScores: {},
    skills: {},
    saves: {},
    ac: 0,
    initiative: 0,
    speed: 0,
    maxHP: 0,
    proficiencyBonus: 0,
    carryCapacity: 0
  };

  feats.forEach(featRef => {
    // [P1.1 — APR 2026]: Pass character.customFeats to getFeatData so
    // custom feats (id: `custom_<timestamp>`) resolve to their full
    // definition (with passiveEffects). Prior code fell through to
    // featRef as the data source, but featRef on playerStats.feats only
    // carries {id, name, customFeat, activeUses, customChoices} — its
    // passiveEffects/effects fields are absent, so the bonuses silently
    // dropped.
    const feat = getFeatData(featRef.id, character?.customFeats) || featRef;

    if (feat.passiveEffects && feat.passiveEffects !== null) {
      // [FIX 2025-10-12]: Handle both OBJECT and ARRAY structures
      // OBJECT structure: { initiative: 5, speedBonus: 10, ... }
      // ARRAY structure: [{ type: 'initiative', value: 5 }, ...]
      // [FIX 2025-10-12 v2]: Also check !== null because typeof null === 'object' in JavaScript

      if (Array.isArray(feat.passiveEffects)) {
        // ARRAY structure (legacy format)
        feat.passiveEffects.forEach(effect => {
          switch (effect.type) {
            case 'ability':
              const ability = effect.target || effect.ability;
              if (!effects.abilityScores[ability]) {
                effects.abilityScores[ability] = 0;
              }
              effects.abilityScores[ability] += effect.value || 0;
              break;

            case 'save':
              const saveType = effect.target || effect.save;
              if (!effects.saves[saveType]) {
                effects.saves[saveType] = 0;
              }
              effects.saves[saveType] += effect.value || 0;
              break;

            case 'skill':
              const skill = effect.target || effect.skill;
              if (!effects.skills[skill]) {
                effects.skills[skill] = { bonus: 0, expertise: false, proficiency: false };
              }
              if (effect.expertise) {
                effects.skills[skill].expertise = true;
              }
              if (effect.proficiency) {
                effects.skills[skill].proficiency = true;
              }
              if (effect.bonus) {
                effects.skills[skill].bonus += effect.bonus;
              }
              break;

            case 'ac':
              effects.ac += effect.value || 0;
              break;

            case 'initiative':
              effects.initiative += effect.value || 0;
              break;

            case 'speed':
              effects.speed += effect.value || 0;
              break;

            case 'maxHP':
              effects.maxHP += effect.value || 0;
              break;

            case 'carryCapacity':
              effects.carryCapacity += effect.value || 0;
              break;
          }
        });
      } else if (typeof feat.passiveEffects === 'object' && feat.passiveEffects !== null) {
        // OBJECT structure (current feat definitions)
        // Map flat object properties to effect types
        // [FIX 2025-10-12 v2]: Extra null check because typeof null === 'object' in JavaScript
        Object.entries(feat.passiveEffects).forEach(([key, value]) => {
          // Map property names to effect types
          if (key === 'initiative' && typeof value === 'number') {
            effects.initiative += value;
          } else if (key === 'speedBonus' && typeof value === 'number') {
            effects.speed += value;
          } else if (key === 'hpPerLevel' && typeof value === 'number') {
            // Tough feat: 2 HP per level
            effects.maxHP += value * (character.level || 1);
          } else if (key === 'acBonusWhileDualWielding' && typeof value === 'number') {
            // Dual Wielder: +1 AC when dual wielding (would need to check equipment state)
            // For now, we'll apply it conditionally (this should be enhanced)
            effects.ac += value;
          } else if (key === 'passivePerception' && typeof value === 'number') {
            // Observant feat: +5 to passive Perception
            // Store as skill bonus for Perception
            if (!effects.skills['perception']) {
              effects.skills['perception'] = { bonus: 0, expertise: false, proficiency: false };
            }
            effects.skills['perception'].bonus += value;
          } else if (key === 'passiveInvestigation' && typeof value === 'number') {
            // Observant feat: +5 to passive Investigation
            if (!effects.skills['investigation']) {
              effects.skills['investigation'] = { bonus: 0, expertise: false, proficiency: false };
            }
            effects.skills['investigation'].bonus += value;
          }
          // Add more mappings as needed for other flat properties
          // Most other properties are situational (ignoreLoading, noRangedDisadvantageInMelee, etc.)
          // and should be checked at roll time, not applied as passive bonuses
        });
      }
    }
  });

  return effects;
};

export default SRD_FEATS;